/**
 * Data layer for `offer18-integration`. Thin typed fetch wrappers over /api/v1/offer18-integration.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface Offer18IntegrationRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListOffer18IntegrationResponse {
	count: number;
	items: Offer18IntegrationRow[];
}

export interface CreateOffer18IntegrationBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/offer18-integration";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListOffer18IntegrationResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListOffer18IntegrationResponse>(res);
}

export async function create(body: CreateOffer18IntegrationBody): Promise<Offer18IntegrationRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<Offer18IntegrationRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const offer18IntegrationQueryKeys = {
	all: ["offer18-integration"] as const,
	list: () => [...offer18IntegrationQueryKeys.all] as const,
	detail: (id: string) => [...offer18IntegrationQueryKeys.all, id] as const,
};
